import { Component, computed, input, InputSignal, OnInit, Signal, signal, ChangeDetectionStrategy } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/helper/toast.service';
import { AwModalComponent } from '@components/misc/aw-modal/aw-modal.component';
import { AwButtonComponent } from '@components/misc/aw-button/aw-button.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DialogRef } from '@angular/cdk/dialog';
import { HelperService } from '@services/helper/helper.service';
import { AW } from '@airwallet/types';
import { HomeService } from '@services/home/home.service';
import { LoadingComponent } from '../../loading/loading.component';
import { AwTooltipComponent } from '@components/misc/aw-tooltip/aw-tooltip.component';
import { NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { MixpanelService } from '@services/mixpanel/mixpanel.service';
import { RouterLink } from '@angular/router';
import { AuthService } from '@services/auth/auth.service';

@Component({
    selector: 'app-instant-payout-modal',
    imports: [AwModalComponent, TranslateModule, AwButtonComponent, FormsModule, ReactiveFormsModule, LoadingComponent, AwTooltipComponent, NgSelectComponent, NgOptionTemplateDirective, NgLabelTemplateDirective, RouterLink],
    templateUrl: './quick-payout-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './quick-payout-modal.component.scss'
})
export class QuickPayoutModalComponent implements OnInit {
    loading = signal(false);
    availableFunds: InputSignal<AW.BankAccount.AvailableFunds[]> = input<AW.BankAccount.AvailableFunds[]>([]);
    selectedBankAccount = signal<AW.BankAccount.AvailableFunds | null>(null);
    fee: { amount: number; type: 'percent' | 'minimum' } = { amount: 0, type: 'minimum' };
    totalFeeAmount!: number;
    initLoading = true;
    structuredLabel!: string;
    fireCustomer: Signal<AW.Account | null> = this.authService.customerFirestore;

    formatAmount: Signal<string> = computed(() => {
        const bankAccount = this.selectedBankAccount();
        if (!bankAccount) return '-';
        return this.helperService.localizeNumberWithCurrency(bankAccount.availableFunds / 100, 2, 2, bankAccount.currency);
    });

    formatPrice: Signal<string> = computed(() => {
        const bankAccount = this.selectedBankAccount();
        if (!bankAccount) return '-';
        return this.helperService.localizeNumberWithCurrency(this.fee.amount / 100, 2, 2, bankAccount.currency);
    });

    formatPayoutResult: Signal<string> = computed(() => {
        const bankAccount = this.selectedBankAccount();
        if (!bankAccount) return '-';
        const forPayout = (bankAccount.availableFunds - this.totalFeeAmount) / 100;
        return this.helperService.localizeNumberWithCurrency(forPayout, 2, 2, bankAccount.currency);
    });

    confirmBtnDisabled: Signal<boolean> = computed(() => {
        const bankAccount = this.selectedBankAccount();
        if (!bankAccount) return true;
        return this.loading() || bankAccount.processing_payout || !!bankAccount.pending_activation || bankAccount.availableFunds - this.fee.amount < 0;
    });

    constructor(
        private homeService: HomeService,
        private dialogRef: DialogRef<unknown>,
        private toastService: ToastService,
        protected helperService: HelperService,
        private translate: TranslateService,
        private mixpanelService: MixpanelService,
        private authService: AuthService
    ) {}

    async ngOnInit() {
        this.initLoading = false;
    }

    // Optional: To see changes in console
    onSelectionChange() {
        const bankAccount = this.selectedBankAccount();
        if (!bankAccount) return;
        this.fee = this.calculatePayoutFee(bankAccount.availableFunds, bankAccount.currency);
        this.totalFeeAmount = this.fee.amount;
        const payoutSettings = this.payoutFeeSettings[bankAccount.currency.toUpperCase() as keyof typeof this.payoutFeeSettings];
        this.structuredLabel = `${this.translate.instant('overview.variable')} ${payoutSettings.percent * 100}%, min. ${payoutSettings.minimum / 100} ${bankAccount.currency.toUpperCase()}`;
    }

    confirmQuickPayout() {
        this.loading.set(true);
        this.homeService
            .payoutNow(this.selectedBankAccount()!.docId)
            .then(res => {
                console.log('oayout reponse', res);
                this.mixpanelService.track('QUICK_PAYOUT_CONFIRM_SUCCESS', { source: 'quick_payout_modal' });
                this.toastService.success(this.translate.instant('overview.payout_in_process'));
                this.dialogRef.close('payout_initiated');
            })
            .catch(err => {
                this.mixpanelService.track('QUICK_PAYOUT_CONFIRM_FAILED', { source: 'quick_payout_modal', error: err?.message || err });
                console.error('payout-now/error', err);
                this.toastService.error(this.translate.instant('overview.payout_currently_unavailable'));
                this.loading.set(false);
            });
    }
    formatForSelectBox(baFunds: AW.BankAccount.AvailableFunds): string {
        let str = this.helperService.localizeNumberWithCurrency(baFunds.availableFunds / 100, 2, 2, baFunds.currency) + ' - ' + baFunds.baName;
        if (baFunds.pending_activation) str += ` (${this.translate.instant('account.pending_activation')})`;
        return str;
    }

    payoutFeeSettings = {
        AUD: { percent: 0.02, minimum: 500 },
        EUR: { percent: 0.02, minimum: 200 },
        BSD: { percent: 0.02, minimum: 200 },
        BGN: { percent: 0.02, minimum: 200 },
        CAD: { percent: 0.02, minimum: 200 },
        USD: { percent: 0.02, minimum: 200 },
        CRC: { percent: 0.02, minimum: 100 },
        CZK: { percent: 0.02, minimum: 5000 },
        DKK: { percent: 0.02, minimum: 2000 },
        GBP: { percent: 0.02, minimum: 200 },
        MXN: { percent: 0.02, minimum: 5000 },
        NZD: { percent: 0.02, minimum: 500 },
        NOK: { percent: 0.02, minimum: 2000 },
        PLN: { percent: 0.02, minimum: 1000 },
        RON: { percent: 0.02, minimum: 1000 },
        SGD: { percent: 0.02, minimum: 2000 },
        ZAR: { percent: 0.02, minimum: 5000 },
        SEK: { percent: 0.02, minimum: 2000 },
        CHF: { percent: 0.02, minimum: 200 }
    };
    calculatePayoutFee(amount: number, currency: string): { amount: number; type: 'percent' | 'minimum' } {
        const settings = this.payoutFeeSettings[currency.toUpperCase() as keyof typeof this.payoutFeeSettings];
        if (!settings) throw new Error('payout/calculate-fee/unsupported-currency: Payout fee calculation attempted for unsupported currency: ' + currency);
        const percentFee = Math.round(amount * settings.percent);
        if (percentFee < settings.minimum) return { amount: settings.minimum, type: 'minimum' };
        return { amount: percentFee, type: 'percent' };
    }
}
